import { DISCREPANCY_REASON_LABELS, formatQuantity } from '../../utils/inventoryLabels';
import { toPublicLabelUrl } from '../../utils/s3Utils';
import { isContainer } from '../supplies/suppliesUtils';

/*
 * The rules of preparing an order, outside any component so they can be
 * tested alone: when a step is done, what blocks the exit, what a dialog is
 * missing, and what to tell once the server answered.
 */

/** The four logistic variables of a parcel, in the order they are typed. */
export const LOGISTICS_FIELDS = [
  { key: 'weight_grams', label: 'Peso', unit: 'g' },
  { key: 'length_cm', label: 'Largo', unit: 'cm' },
  { key: 'width_cm', label: 'Ancho', unit: 'cm' },
  { key: 'height_cm', label: 'Alto', unit: 'cm' },
];

/** The reasons a location may not hold what the system said, as options. */
export const DISCREPANCY_REASON_OPTIONS = Object.entries(DISCREPANCY_REASON_LABELS).map(
  ([value, label]) => ({ value, label })
);

/** What a line still lacks, as a number. */
export const pendingOf = (line) => Number(line?.pending || 0);

/** What the line's reservation still promises, as a number. */
export const reservedOf = (line) => Number(line?.reserved_quantity || 0);

/**
 * Why a line cannot be picked, or `null`. Nothing is picked that is not
 * reserved: the reservation is the promise that the stock is this order's,
 * and picking past it takes units another order already holds. The server
 * refuses it too; saying it on the button spares the trip.
 */
export const pickBlocker = (line) => {
  if (pendingOf(line) === 0) return null;
  if (!line?.suggestions?.length) return 'Sin stock pickeable';
  if (reservedOf(line) <= 0) return 'Falta reservar';
  return null;
};

/**
 * Why the order cannot be reserved (again), or `null`. "Reservada" when every
 * line that still lacks something has its reservation covering it: pressing
 * the button then would only answer that there was nothing to do.
 */
export const reserveBlocker = ({ lines }) => {
  if (!lines || lines.length === 0) return 'Sin líneas';
  const covered = lines.every((line) => reservedOf(line) >= pendingOf(line));
  return covered ? 'Reservada' : null;
};

/** A parcel is closed when it has its weight and its three dimensions. */
export const isPackageComplete = (pkg) =>
  LOGISTICS_FIELDS.every((field) => Number(pkg?.[field.key]) > 0);

/** Picking ends when there are lines and none of them lacks anything. */
export const isPickingDone = (lines) => lines.length > 0 && lines.every((line) => pendingOf(line) === 0);

/** Packing ends when there are parcels and all of them are closed. */
export const isPackingDone = (packages) => packages.length > 0 && packages.every(isPackageComplete);

/**
 * Which of the three steps the order is on: 0 picking, 1 packing, 2 exit.
 *
 * An order without lines has nothing to pick, so it starts on the parcels.
 */
export const currentStep = ({ lines, packages }) => {
  const pickingDone = lines.length === 0 || isPickingDone(lines);
  if (!pickingDone) return 0;
  return isPackingDone(packages) ? 2 : 1;
};

/**
 * Why the order cannot leave yet, or `null` when it can.
 *
 * Dispatching is refused by the server for the same two reasons; saying them
 * on the button spares a round trip and a red message.
 */
export const shipBlocker = ({ lines, packages }) => {
  if (lines.length > 0 && !isPickingDone(lines)) return 'Falta terminar de pickear';
  if (packages.length === 0) return 'Falta armar los bultos';
  if (!isPackingDone(packages)) return 'Falta cerrar los bultos';
  return null;
};

/**
 * How long the screen waits for the courier's answer over the socket before it
 * says so and reloads on its own. A courier takes seconds; a minute and a half
 * of silence means the socket is down or the queue is, and either way the
 * tracking will be on the order whenever it arrives.
 */
export const LABEL_WAIT_MS = 90 * 1000;

/** The labels of a courier answer, one per parcel, ready to fetch. */
export const labelResultUrls = (result) =>
  (result?.labels || []).map((label) => (label.url ? toPublicLabelUrl(label.url) : null)).filter(Boolean);

/**
 * The courier's answer as the order and its parcels carry it, in the shape the
 * socket event has: `CourierLabels` reads one and the same thing whether it
 * just landed or it is being read back a day later.
 *
 * @param {object|null} order - The WMS order (`courier`, `tracking_number`, `url_tracking`).
 * @param {Array<object>} packages - Parcels with `item_index`, `tracking_number` and `label`.
 */
export const courierSummaryOf = (order, packages = []) => ({
  courier: order?.courier ?? null,
  tracking_number: order?.tracking_number ?? null,
  url_tracking: order?.url_tracking ?? null,
  labels: (packages || []).map((pkg) => ({
    item_index: pkg.item_index,
    tracking_number: pkg.tracking_number ?? null,
    url: pkg.label ?? null,
  })),
});

/** Whether the courier has said anything yet: a tracking, or at least one label. */
export const hasCourierAnswer = (summary) =>
  Boolean(summary?.tracking_number) || (summary?.labels || []).some((label) => label.url);

/** What to tell when the courier refused. The task's own sentence wins. */
export const labelErrorNotice = (result) => ({
  type: 'error',
  message: result?.message || 'El courier rechazó el envío.',
});

/** What to tell when the socket stayed silent for too long. */
export const LABEL_WAIT_NOTICE = {
  type: 'info',
  message:
    'El courier todavía no respondió. El seguimiento y las etiquetas aparecen en la orden apenas lo haga.',
};

/**
 * Why the label cannot be asked for yet: the exit rules, and a label that
 * already exists is not asked for twice. An order without lines gets its
 * label like any other — that is most of the OMS's orders.
 */
export const labelBlocker = ({ order, lines, packages }) => {
  if (order?.tracking_number) return 'Ya tiene etiqueta';
  return shipBlocker({ lines, packages });
};

/**
 * Volumetric weight in kilograms, or `null` while a dimension is missing.
 * Couriers charge the greater of the real weight and this one.
 */
export const volumetricKg = (values) => {
  const length = Number(values?.length_cm);
  const width = Number(values?.width_cm);
  const height = Number(values?.height_cm);
  if (!(length > 0 && width > 0 && height > 0)) return null;
  return (length * width * height) / 4000;
};

/** The real weight of every parcel together, in kilograms. */
export const totalWeightKg = (packages) =>
  packages.reduce((total, pkg) => total + Number(pkg.weight_grams || 0), 0) / 1000;

/** How many parcels are closed, as "2 de 3 bultos cerrados". */
export const packingSummary = (packages) => {
  const closed = packages.filter(isPackageComplete).length;
  return packages.length === 1
    ? `${closed} de 1 bulto cerrado`
    : `${closed} de ${packages.length} bultos cerrados`;
};

/**
 * Whether a lot has to be chosen: the location holds lots, so the server needs
 * to know which one leaves.
 */
export const lotRequired = (suggestion) => Boolean(suggestion?.lots?.length);

/**
 * The lot a location opens on: with a single lot there is nothing to choose,
 * so it is chosen; with several, the first is the one FEFO says but the
 * picker confirms it.
 */
export const defaultLotOf = (suggestion) =>
  suggestion?.lots?.length === 1 ? suggestion.lots[0].lot : '';

/**
 * Why a pick cannot be sent yet, or `null`. Written for the button.
 *
 * @param {{suggestion: object|null, quantity: string|number, max: number, lot: string}} params
 */
export const pickProblem = ({ suggestion, quantity, max, lot, reserved }) => {
  if (!suggestion) return 'Elige de dónde sacar';
  const amount = Number(quantity);
  if (!quantity || !(amount > 0)) return 'Indica cuántas';
  if (!Number.isInteger(amount)) return 'Solo unidades enteras';
  if (amount > max) return `Máximo ${formatQuantity(max)}`;
  // Under what is pending, the reservation caps what can be picked.
  if (reserved !== undefined && amount > reserved) return `Solo ${formatQuantity(reserved)} reservadas`;
  if (lotRequired(suggestion) && !lot) return 'Elige el lote';
  const available = lot
    ? Number(suggestion.lots.find((item) => item.lot === lot)?.quantity || 0)
    : Number(suggestion.available || 0);
  if (amount > available) return `En ${suggestion.slot_name} hay ${formatQuantity(available)}`;
  return null;
};

/** Why a discrepancy cannot be reported yet, or `null`. */
export const shortPickProblem = ({ suggestion, found, reason }) => {
  if (!suggestion) return 'Elige la ubicación';
  if (found === '' || found === null || found === undefined) return 'Indica cuánto había';
  const amount = Number(found);
  if (!(amount >= 0)) return 'Indica cuánto había';
  if (!Number.isInteger(amount)) return 'Solo unidades enteras';
  if (!reason) return 'Elige el motivo';
  return null;
};

/** Why picked units cannot go back yet, or `null`. */
export const unpickProblem = ({ slotId, quantity, max }) => {
  if (!slotId) return 'Elige a dónde vuelve';
  const amount = Number(quantity);
  if (!quantity || !(amount > 0)) return 'Indica cuántas';
  if (!Number.isInteger(amount)) return 'Solo unidades enteras';
  if (amount > max) return `Máximo ${formatQuantity(max)}`;
  return null;
};

export const buildPickPayload = ({ suggestion, quantity, lot, scannedCode }) => {
  const payload = { slot_id: suggestion.slot_id, quantity: Number(quantity), lot: lot || '' };
  if (scannedCode?.trim()) payload.scanned_code = scannedCode.trim();
  return payload;
};

export const buildShortPickPayload = ({ suggestion, found, reason, lot, notes }) => {
  const payload = {
    slot_id: suggestion.slot_id,
    found_quantity: Number(found),
    reason,
    lot: lot || '',
  };
  if (notes?.trim()) payload.notes = notes.trim();
  return payload;
};

export const buildUnpickPayload = ({ slotId, quantity, lot, reason }) => ({
  slot_id: Number(slotId),
  quantity: Number(quantity),
  lot: lot || '',
  reason: reason || 'Corrección desde Preparar orden',
});

/**
 * The parcel fields that changed, as whole numbers, or nothing when none did.
 * The server takes integers and refuses negatives.
 */
export const changedLogistics = (pkg, values) => {
  const changed = {};
  LOGISTICS_FIELDS.forEach(({ key }) => {
    const raw = values[key];
    if (raw === '' || raw === null || raw === undefined) return;
    const amount = Number(raw);
    if (!Number.isInteger(amount) || amount < 0) return;
    if (amount !== Number(pkg[key] || 0)) changed[key] = amount;
  });
  return Object.keys(changed).length > 0 ? changed : null;
};

/**
 * How a packing supply reads in a list: "CAJA-M — Caja mediana · 30×30×30 ·
 * quedan 95". A container says its size, because that is what the picker is
 * choosing by.
 */
export const supplyOptionLabel = (supply) => {
  const size =
    isContainer(supply) && supply.length_cm && supply.width_cm && supply.height_cm
      ? ` · ${supply.length_cm}×${supply.width_cm}×${supply.height_cm}`
      : '';
  return `${supply.sku} — ${supply.name}${size} · quedan ${formatQuantity(supply.quantity)}`;
};

/** The grams of packaging in a parcel: the tare of every container used in it, times how many. */
export const packageTareGrams = (pkg) =>
  (pkg?.supplies || [])
    .filter((row) => isContainer(row) && Number(row.tare_grams) > 0)
    .reduce((total, row) => total + Number(row.tare_grams) * Number(row.quantity || 0), 0);

/** The units of each line in this parcel, as the inputs hold them. */
export const packageProductsForm = (pkg) =>
  (pkg?.item_products || []).reduce((form, assignment) => {
    form[assignment.order_product] = String(assignment.prepared_quantity);
    return form;
  }, {});

/**
 * Units of `line` this parcel may still take: what was picked minus what the
 * other parcels already claim. The same unit cannot travel in two boxes.
 */
export const availableForLine = (line, packages, pkg) => {
  const claimedElsewhere = (packages || [])
    .filter((other) => other.id !== pkg?.id)
    .reduce((sum, other) => {
      const assignment = (other.item_products || []).find(
        (entry) => entry.order_product === line.order_product_id
      );
      return sum + Number(assignment?.prepared_quantity || 0);
    }, 0);
  return Math.max(Number(line.prepared_quantity || 0) - claimedElsewhere, 0);
};

/**
 * The parcel's contents to send, or `null` when nothing changed. Every picked
 * line travels, zero included, so a line taken out of the parcel is deleted
 * on the server rather than left as it was.
 */
export const changedProducts = (pkg, quantities, lines) => {
  const current = packageProductsForm(pkg);
  const products = (lines || [])
    .filter((line) => Number(line.prepared_quantity) > 0)
    .map((line) => {
      const raw = quantities[line.order_product_id];
      const amount = raw === '' || raw === undefined || raw === null ? 0 : Number(raw);
      return {
        order_product_id: line.order_product_id,
        prepared_quantity: Number.isInteger(amount) && amount >= 0 ? amount : 0,
      };
    });
  const changed = products.some(
    (entry) => entry.prepared_quantity !== Number(current[entry.order_product_id] || 0)
  );
  return changed ? products : null;
};

/**
 * What the parcel should weigh, as the server computed it: "Sugerido 850 g:
 * 500 g de caja + 350 g de productos". Nothing while there is nothing to add.
 */
export const weightSuggestionNote = (pkg) => {
  const suggestion = pkg?.weight_suggestion;
  if (!suggestion || !(suggestion.total > 0)) return null;
  const parts = [];
  if (suggestion.tare_grams > 0) parts.push(`${suggestion.tare_grams} g de caja`);
  if (suggestion.products_grams > 0) parts.push(`${suggestion.products_grams} g de productos`);
  return `Sugerido ${suggestion.total} g: ${parts.join(' + ')}`;
};

/**
 * The products that weigh nothing in the catalogue, so the suggestion is not
 * taken for a complete weight. The parcel closes all the same: keeping that
 * data is the client's job.
 */
export const unweighedNote = (pkg) => {
  const skus = pkg?.weight_suggestion?.unweighed || [];
  if (skus.length === 0) return null;
  return `Sin peso en el maestro, suman 0 g: ${skus.join(', ')}. Corrígelo en Productos.`;
};

/**
 * What to say when a box lent the parcel its numbers. The fields are already
 * on screen; the point is that they did not appear out of nowhere and can be
 * corrected.
 */
export const boxFilledNotice = (supply, filled) => {
  const dims = filled.includes('length_cm');
  const weight = filled.includes('weight_grams');
  if (!dims && !weight) return null;
  const what = dims && weight ? 'Medidas y peso' : dims ? 'Medidas' : 'Peso';
  return `${what} del bulto tomados de ${supply.sku}; corrígelos si no corresponden.`;
};

/**
 * The supply a typed or scanned code names, or `null`. The code has to match
 * a barcode or a SKU whole: a scanner reads the whole label, and a prefix
 * that matched two boxes would put the wrong one on the invoice.
 */
export const resolveSupplyCode = (typed, options) => {
  const code = (typed || '').trim().toLowerCase();
  if (!code) return null;
  return (
    options.find((supply) => (supply.barcode || '').toLowerCase() === code) ||
    options.find((supply) => supply.sku.toLowerCase() === code) ||
    null
  );
};

/** Why a supply cannot go into the parcel yet, or `null`. Written for the button. */
export const packageSupplyProblem = ({ supply, quantity, typed }) => {
  if (!supply) return typed?.trim() ? 'Ese código no es de un insumo' : 'Elige el insumo';
  const amount = Number(quantity);
  if (!quantity || !(amount > 0)) return 'Indica cuántos';
  if (amount > Number(supply.quantity)) return `Quedan ${formatQuantity(supply.quantity)}`;
  return null;
};

/** The supplies of a parcel as one line: "Caja mediana ×1 · Cinta ×2". */
export const packageSuppliesSummary = (pkg) =>
  (pkg?.supplies || []).map((row) => `${row.name} ×${formatQuantity(row.quantity)}`).join(' · ');

/** The parcel form, seeded from the parcel; empty strings where nothing is set. */
export const logisticsFormFrom = (pkg) =>
  LOGISTICS_FIELDS.reduce((form, { key }) => ({ ...form, [key]: pkg?.[key] ?? '' }), {});

/**
 * What to say after reserving: the server answers with what it added and how
 * many lines ended up complete. Nothing added is told as information, not as
 * success — the stock was already committed, or there is none.
 */
export const reserveDoneNotice = (result) => {
  const added = Number(result?.reserved_now || 0);
  if (added === 0) {
    return {
      type: 'success',
      message: 'No había nada que reservar: el stock ya estaba comprometido, o no hay disponible.',
    };
  }
  const complete = result.lines_complete;
  const total = result.lines_total;
  return {
    type: 'success',
    message: `${formatQuantity(added)} un reservadas · ${complete} de ${total} línea${
      total === 1 ? '' : 's'
    } completa${complete === 1 ? '' : 's'}`,
  };
};

export const pickDoneNotice = ({ line, quantity, suggestion, capacityWarning }) => ({
  type: 'success',
  message: [
    `${formatQuantity(quantity)} un de ${line.sku} sacadas de ${suggestion.slot_name}`,
    capacityWarning,
  ]
    .filter(Boolean)
    .join(' · '),
});

export const shortPickDoneNotice = ({ line, found, suggestion }) => ({
  type: 'success',
  message:
    Number(found) > 0
      ? `Desvío registrado en ${suggestion.slot_name} · ${formatQuantity(found)} un de ${line.sku} sacadas`
      : `Desvío registrado en ${suggestion.slot_name} · queda una tarea de conteo`,
});
